import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  MapPinIcon, 
  PlusIcon, 
  Bars3Icon, 
  XMarkIcon,
  HeartIcon,
  UserIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import SearchBar from './SearchBar';
import { useAnalytics } from '../services/analytics';

function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { trackUserFlow } = useAnalytics();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowSearchBar(false);
  }, [location.pathname]);

  // Show search bar on search page
  useEffect(() => {
    setShowSearchBar(location.pathname === '/search');
  }, [location.pathname]);

  const handleSearchSubmit = (searchData) => {
    const params = new URLSearchParams();
    
    if (searchData.query) params.set('q', searchData.query);
    if (searchData.location) params.set('location', searchData.location);
    if (searchData.lat && searchData.lng) {
      params.set('lat', searchData.lat);
      params.set('lng', searchData.lng);
    }

    trackUserFlow('header_search', 'search_flow');
    navigate(`/search?${params.toString()}`);
  };

  const toggleTheme = () => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle('dark');
  };

  const navigation = [
    { name: 'Discover', href: '/search', icon: MagnifyingGlassIcon },
    { name: 'Map View', href: '/map', icon: MapPinIcon },
    { name: 'Add Restaurant', href: '/submit', icon: PlusIcon },
  ];

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-white/90 backdrop-blur-md shadow-header border-b border-surface-200/50' 
        : 'bg-white shadow-sm border-b border-surface-200'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl blur opacity-75 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative bg-gradient-to-r from-primary-600 to-primary-700 text-white p-2.5 rounded-xl group-hover:scale-105 transition-transform">
                  <MapPinIcon className="h-6 w-6" />
                </div>
              </div>
              <div className="hidden sm:block">
                <div className="flex items-baseline space-x-2">
                  <span className="font-bold text-2xl text-surface-900 tracking-tight">KosherEats</span>
                  <span className="px-2 py-0.5 bg-gold-100 text-gold-800 text-xs font-semibold rounded-full">
                    VERIFIED
                  </span>
                </div>
                <div className="text-xs text-surface-500 -mt-1 font-medium">
                  Discover Kosher. Anywhere.
                </div>
              </div>
              <div className="sm:hidden">
                <span className="font-bold text-xl text-surface-900">KosherEats</span>
              </div>
            </Link>
          </div>

          {/* Search Bar (Desktop) */}
          {showSearchBar && (
            <div className="hidden md:block flex-1 max-w-2xl mx-8">
              <SearchBar 
                onSubmit={handleSearchSubmit}
                placeholder="Search restaurants, locations, or cuisine..."
                compact
              />
            </div>
          )}

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href || 
                             (item.href === '/search' && location.pathname.startsWith('/search'));
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group relative flex items-center space-x-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    isActive
                      ? 'bg-primary-100 text-primary-700 shadow-sm'
                      : 'text-surface-600 hover:text-surface-900 hover:bg-surface-100'
                  }`}
                >
                  {Icon && (
                    <Icon className={`h-4 w-4 transition-colors ${
                      isActive ? 'text-primary-600' : 'text-surface-500 group-hover:text-surface-700'
                    }`} />
                  )}
                  <span>{item.name}</span>
                  {isActive && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary-600 rounded-full"></div>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-2">
            {/* Theme toggle (desktop) */}
            <button
              onClick={toggleTheme}
              className="hidden lg:flex items-center justify-center w-10 h-10 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-xl transition-all duration-200 focus-ring"
              title="Toggle theme"
            >
              {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </button>

            {/* Favorites (desktop) */}
            <button className="hidden lg:flex items-center justify-center w-10 h-10 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-xl transition-all duration-200 focus-ring">
              <HeartIcon className="h-5 w-5" />
            </button>

            {/* User account (desktop) */}
            <button className="hidden lg:flex items-center justify-center w-10 h-10 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-xl transition-all duration-200 focus-ring">
              <UserIcon className="h-5 w-5" />
            </button>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center space-x-1">
              {/* Mobile search button */}
              {!showSearchBar && (
                <button
                  onClick={() => setShowSearchBar(true)}
                  className="flex items-center justify-center w-10 h-10 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-xl transition-all duration-200"
                  aria-label="Search"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </button>
              )}
              
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="flex items-center justify-center w-10 h-10 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-xl transition-all duration-200"
                aria-label="Open menu"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Search Bar */}
        {showSearchBar && (
          <div className="lg:hidden pb-4 px-4">
            <SearchBar 
              onSubmit={handleSearchSubmit}
              placeholder="Search restaurants, cuisine, location..."
              onCancel={() => setShowSearchBar(false)}
              autoFocus
            />
          </div>
        )}
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-surface-200/50 shadow-dropdown">
          <div className="px-4 py-4 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href || 
                             (item.href === '/search' && location.pathname.startsWith('/search'));
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 ${
                    isActive
                      ? 'bg-primary-100 text-primary-700 shadow-sm'
                      : 'text-surface-700 hover:bg-surface-100 hover:text-surface-900'
                  }`}
                >
                  {Icon && (
                    <Icon className={`h-5 w-5 ${
                      isActive ? 'text-primary-600' : 'text-surface-500'
                    }`} />
                  )}
                  <span>{item.name}</span>
                </Link>
              );
            })}
            
            {/* Mobile-only actions */}
            <div className="pt-4 mt-4 border-t border-surface-200">
              <div className="flex items-center space-x-4 px-4">
                <button
                  onClick={toggleTheme}
                  className="flex items-center space-x-2 text-surface-600 hover:text-surface-900"
                >
                  {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
                  <span className="text-sm font-medium">Theme</span>
                </button>
                
                <button className="flex items-center space-x-2 text-surface-600 hover:text-surface-900">
                  <HeartIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Favorites</span>
                </button>

                <button className="flex items-center space-x-2 text-surface-600 hover:text-surface-900">
                  <UserIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Account</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;